<template>
    <div class="data-table-view">
        <div class="table-toolbar">
            <span class="p-input-icon-left">
                <i class="pi pi-search" />
                <InputText
                    v-model="searchText"
                    placeholder="Поиск по всем столбцам..."
                    class="p-inputtext-sm filter-input"
                    @input="onSearchInput"
                />
            </span>
            <PrimeButton
                v-if="editable"
                type="button"
                label="Добавить строку"
                icon="pi pi-plus"
                class="p-button-sm p-button-outlined"
                @click.prevent="addRow"
            />
        </div>
        <DataTable
            :value="rows"
            responsiveLayout="scroll"
            showGridlines
            :scrollable="true"
            scrollHeight="500px"
            :loading="loading"
            editMode="cell"
            class="p-datatable-sm"
            :paginator="true"
            :rows="perPage"
            :totalRecords="totalRecords"
            :rowsPerPageOptions="[25, 50, 100]"
            :lazy="true"
            @page="onPage"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        >
            <TableColumn
                class="narrow-column index-column"
                headerStyle="width: 120px; min-width: 120px; max-width: 120px; text-align: center"
                bodyStyle="width: 120px; min-width: 120px; max-width: 120px; text-align: center"
            >
                <template #header>
                    <span>#</span>
                </template>
                <template #body="{ data }">
                    {{ data.row }}
                </template>
            </TableColumn>

            <TableColumn
                v-if="showPdfColumn"
                class="narrow-column"
                headerStyle="width: 60px; min-width: 60px; max-width: 60px; text-align: center"
                bodyStyle="width: 60px; min-width: 60px; max-width: 60px; text-align: center"
            >
                <template #header>
                    <i class="pi pi-file-pdf"></i>
                </template>
                <template #body="{ data }">
                    <i class="pi pi-file-pdf cursor-pointer" @click="$emit('download-one', data)"></i>
                </template>
            </TableColumn>

            <TableColumn
                v-for="col in columns"
                :key="col.column"
                :field="col.column"
                :header="col.name"
                style="width: 200px"
            >
                <template #body="{ data, field }">
                    <template v-if="editable">
                        <InputText
                            v-if="editingCell.row === data.row && editingCell.column === field"
                            v-model="editingCell.value"
                            class="p-inputtext-sm cell-input"
                            @blur="saveCell(data)"
                            @keydown.enter.prevent="saveCell(data)"
                            @keydown.tab.prevent="saveCellAndMove(data, field, $event.shiftKey ? -1 : 1)"
                            @keyup.escape="cancelEdit"
                            autofocus
                        />
                        <span
                            v-else
                            class="cell-display"
                            @click="startEdit(data, field, data[field])"
                        >
                            {{ data[field] || '—' }}
                        </span>
                    </template>
                    <template v-else>
                        <span>{{ data[field] || '—' }}</span>
                    </template>
                </template>
            </TableColumn>

            <TableColumn
                v-if="showActions && editable"
                class="narrow-column"
                headerStyle="width: 60px; min-width: 60px; max-width: 60px; text-align: center"
                bodyStyle="width: 60px; min-width: 60px; max-width: 60px; text-align: center"
            >
                <template #header>
                    <i class="pi pi-cog"></i>
                </template>
                <template #body="{ data }">
                    <PrimeButton
                        icon="pi pi-trash"
                        type="button"
                        class="p-button-sm p-button-text p-button-danger"
                        @click.prevent="confirmDelete(data)"
                    />
                </template>
            </TableColumn>
        </DataTable>

        <div class="table-footer">
            <div class="row-count">
                Строк: {{ totalRecords }}
            </div>
        </div>

        <ConfirmDialog />
    </div>
</template>

<script>
import api from "@/libs/api";
import { nextTick } from "vue";

export default {
    name: "DataTableView",
    props: {
        columns: {
            type: Array,
            default: () => []
        },
        projectId: {
            type: Number,
            required: true
        },
        editable: {
            type: Boolean,
            default: true
        },
        showPdfColumn: {
            type: Boolean,
            default: true
        },
        showActions: {
            type: Boolean,
            default: true
        }
    },
    emits: ['row-added', 'row-updated', 'row-deleted', 'download-one'],
    data() {
        return {
            rows: [],
            loading: false,
            searchText: '',
            searchDebounce: null,
            currentPage: 1,
            perPage: 50,
            totalRecords: 0,
            editingCell: {
                row: null,
                column: null,
                value: null,
                originalValue: null
            },
            navigatingCell: false
        };
    },
    mounted() {
        this.fetchRows();
    },
    methods: {
        async fetchRows() {
            this.loading = true;
            try {
                const data = await api.viewProjectData(this.projectId, {
                    page: this.currentPage,
                    per_page: this.perPage,
                    search: this.searchText || undefined
                });
                this.rows = data.values || [];
                if (data.pagination) {
                    this.totalRecords = data.pagination.total;
                }
            } catch (err) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Ошибка',
                    detail: 'Не удалось загрузить данные',
                    life: 5000
                });
            } finally {
                this.loading = false;
            }
        },
        onPage(event) {
            this.currentPage = event.page + 1;
            this.perPage = event.rows;
            this.fetchRows();
        },
        onSearchInput() {
            clearTimeout(this.searchDebounce);
            this.searchDebounce = setTimeout(() => {
                this.currentPage = 1;
                this.fetchRows();
            }, 400);
        },
        async addRow() {
            try {
                const result = await api.addDataRow(this.projectId, {});
                const newRow = result.data || result;
                this.rows.unshift(newRow);
                this.totalRecords++;
                this.$emit('row-added', newRow);
                this.$toast.add({
                    severity: 'success',
                    summary: 'Добавлено',
                    detail: 'Новая строка добавлена',
                    life: 2000
                });
                await nextTick();
                this.focusNewRow(newRow);
            } catch (err) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Ошибка',
                    detail: 'Не удалось добавить строку',
                    life: 5000
                });
            }
        },
        startEdit(data, column, value) {
            this.editingCell = {
                row: data.row,
                column: column,
                value: value || '',
                originalValue: value
            };
        },
        saveCell(data = null, force = false) {
            if (this.navigatingCell && !force) return data;
            if (!this.editingCell.row) return data;

            const { row, column, value } = this.editingCell;
            const originalValue = this.editingCell.originalValue;
            const currentData = data || this.rows.find(item => item.row === row);

            this.editingCell = { row: null, column: null, value: null, originalValue: null };

            if (value === originalValue) return currentData;
            if (!currentData) return data;

            currentData[column] = value;

            api.updateDataRow(this.projectId, row, { [column]: value })
                .then(result => {
                    Object.assign(currentData, result.data);
                })
                .catch(() => {
                    currentData[column] = originalValue;
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Ошибка',
                        detail: 'Не удалось обновить значение',
                        life: 5000
                    });
                });

            return currentData;
        },
        saveCellAndMove(data, field, direction) {
            const currentColumnIndex = this.columns.findIndex(col => col.column === field);
            const nextColumn = this.columns[currentColumnIndex + direction];
            const savedData = this.saveCell(data, true);

            if (!nextColumn) {
                return;
            }

            this.navigatingCell = true;
            const nextData = savedData || data;

            this.$nextTick(() => {
                this.startEdit(nextData, nextColumn.column, nextData[nextColumn.column]);
                this.navigatingCell = false;
                this.$nextTick(() => {
                    const input = this.$el.querySelector('.cell-input');
                    if (input) input.focus();
                });
            });
        },
        cancelEdit() {
            this.editingCell = { row: null, column: null, value: null, originalValue: null };
        },
        focusNewRow(rowData) {
            const firstColumn = this.columns[0];
            if (!firstColumn) {
                return;
            }

            const row = this.rows.find(r => r.row === rowData.row);
            if (!row) return;

            this.startEdit(row, firstColumn.column, row[firstColumn.column]);
            this.$nextTick(() => {
                const input = this.$el.querySelector('.cell-input');
                if (input) {
                    input.focus();
                    if (typeof input.select === 'function') {
                        input.select();
                    }
                }
            });
        },
        confirmDelete(data) {
            this.$confirm.require({
                message: `Удалить строку #${data.row}?`,
                header: 'Подтверждение',
                icon: 'pi pi-exclamation-triangle',
                accept: () => this.deleteRow(data)
            });
        },
        async deleteRow(data) {
            try {
                await api.deleteDataRow(this.projectId, data.row);
                this.rows = this.rows.filter(r => r.row !== data.row);
                this.totalRecords--;
                this.$emit('row-deleted', data.row);
                this.$toast.add({
                    severity: 'success',
                    summary: 'Удалено',
                    detail: 'Строка удалена',
                    life: 2000
                });
            } catch (err) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Ошибка',
                    detail: 'Не удалось удалить строку',
                    life: 5000
                });
            }
        }
    }
}
</script>

<style scoped>
.data-table-view {
    margin-bottom: 16px;
}

.table-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.filter-input {
    width: 300px;
}

.cell-display {
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    transition: background 0.2s;
}

.cell-display:hover {
    background: #e9ecef;
}

.cell-input {
    width: 100%;
}

:deep(.narrow-column) {
    width: 60px;
    min-width: 60px;
    max-width: 60px;
}

.table-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 12px;
}

.row-count {
    color: #6c757d;
    font-size: 0.9rem;
}

.cursor-pointer {
    cursor: pointer;
    color: #3b82f6;
}

.cursor-pointer:hover {
    color: #2563eb;
}
</style>
